<template>
  <div class="space-y-6">
    <h1 class="text-2xl font-bold text-gray-800">{{ $t("otp_management") }}</h1>

    <!-- Actions -->
    <div class="flex gap-3">
      <BaseButton type="primary" @click="fetchOtps">
        {{ $t("refresh") }}
      </BaseButton>

      <select
        v-model="filterStatus"
        class="border rounded px-3 py-2 text-sm"
        @change="fetchOtps"
      >
        <option value="all">{{ $t("all") }}</option>
        <option value="active">{{ $t("active") }}</option>
        <option value="expired">{{ $t("expired") }}</option>
        <option value="used">{{ $t("used") }}</option>
      </select>
    </div>

    <SearchBar v-model="search" @search="fetchOtps" />

    <!-- Stats -->
    <div class="grid grid-cols-4 gap-3">
      <div class="bg-gray-50 rounded-lg p-4">
        <p class="text-xs text-gray-500">{{ $t("total") }}</p>
        <p class="text-2xl font-bold text-gray-800">{{ meta.total ?? "—" }}</p>
      </div>
      <div class="bg-green-50 rounded-lg p-4">
        <p class="text-xs text-gray-500">{{ $t("active") }}</p>
        <p class="text-2xl font-bold text-green-600">
          {{ meta.active ?? "—" }}
        </p>
      </div>
      <div class="bg-red-50 rounded-lg p-4">
        <p class="text-xs text-gray-500">{{ $t("expired") }}</p>
        <p class="text-2xl font-bold text-red-600">{{ meta.expired ?? "—" }}</p>
      </div>
      <div class="bg-gray-50 rounded-lg p-4">
        <p class="text-xs text-gray-500">{{ $t("used") }}</p>
        <p class="text-2xl font-bold text-gray-400">{{ meta.used ?? "—" }}</p>
      </div>
    </div>

    <!-- Table -->
    <BaseDataTable :columns="columns" :data="otps">
      <!-- المستخدم -->
      <template #user_name="{ row }">
        <div>
          <p class="font-medium text-gray-800">{{ row.user_name }}</p>
          <p class="text-xs text-gray-400 font-mono">#{{ row.user_id }}</p>
        </div>
      </template>

      <!-- رمز OTP -->
      <template #code="{ row }">
        <span class="font-mono font-bold tracking-widest text-gray-900">
          {{ row.code }}
        </span>
      </template>

      <!-- الحالة -->
      <template #status="{ row }">
        <span
          class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold"
          :class="{
            'bg-green-100 text-green-700': row.status === 'active',
            'bg-red-100   text-red-700': row.status === 'expired',
            'bg-gray-100  text-gray-500': row.status === 'used',
          }"
        >
          {{ statusLabel(row.status) }}
        </span>
      </template>

      <!-- وقت الإنشاء -->
      <template #created_at="{ row }">
        <span class="text-sm text-gray-500">{{
          formatTime(row.created_at)
        }}</span>
      </template>

      <!-- الصلاحية -->
      <template #expires_at="{ row }">
        <span
          class="text-sm"
          :class="{
            'text-yellow-600 font-medium': isExpiringSoon(row),
            'text-gray-400': row.status !== 'active',
            'text-gray-700': row.status === 'active' && !isExpiringSoon(row),
          }"
        >
          {{ expiresLabel(row) }}
        </span>
      </template>

      <!-- إجراء -->
      <template #actions="{ row }">
        <div class="flex gap-2">
          <button
            class="text-xs px-3 py-1.5 rounded border border-gray-200 hover:bg-gray-50 transition"
            @click="copyCode(row)"
          >
            {{ copiedId === row.id ? "✓ تم النسخ" : "نسخ الكود" }}
          </button>

          <button
            class="text-xs px-3 py-1.5 rounded bg-gray-900 text-white hover:bg-gray-700 transition disabled:opacity-40"
            :disabled="resendingId === row.user_id"
            @click="resend(row)"
          >
            {{ resendingId === row.user_id ? "جاري..." : "إرسال" }}
          </button>

          <button
            class="text-xs px-3 py-1.5 rounded bg-red-600 text-white hover:bg-red-700 transition disabled:opacity-40"
            :disabled="deletingId === row.user_id"
            @click="deleteOtp(row)"
          >
            {{ deletingId === row.user_id ? "جاري..." : "حذف" }}
          </button>
        </div>
      </template>
    </BaseDataTable>

    <BasePagination
      v-if="pagination"
      :pagination="pagination"
      @change-page="fetchOtps"
    />
  </div>
</template>

<script>
import BaseButton from "@/components/Button.vue";
import BaseDataTable from "@/components/BaseDataTable.vue";
import BasePagination from "@/components/BasePagination.vue";
import SearchBar from "@/components/SearchBar.vue";
import otpService from "@/services/otp.service";
import { alertSuccess, alertError, alertConfirm } from "@/utils/alert";

export default {
  name: "OtpList",

  components: {
    BaseButton,
    BaseDataTable,
    BasePagination,
    SearchBar,
  },

  data() {
    return {
      otps: [],
      pagination: null,
      meta: {},
      search: "",
      filterStatus: "all",
      copiedId: null,
      resendingId: null,
      deletingId: null,
    };
  },

  computed: {
    columns() {
      return [
        { label: "#", field: "id", sortable: true },
        { label: this.$t("user"), field: "user_name", sortable: false },
        { label: this.$t("otp_code"), field: "code", sortable: false },
        { label: this.$t("status"), field: "status", sortable: false },
        { label: this.$t("created_at"), field: "created_at", sortable: true },
        { label: this.$t("expires_at"), field: "expires_at", sortable: false },
      ];
    },
  },

  methods: {
    /* ============ Fetch ============ */
    async fetchOtps(page = 1) {
      const res = await otpService.getOtps({
        page,
        per_page: 10,
        search: this.search,
        filter: this.filterStatus,
      });

      this.otps = res.data.data;
      this.meta = res.data.meta;
      this.pagination = res.data.pagination;
    },

    /* ============ Copy ============ */
    async copyCode(row) {
      await navigator.clipboard.writeText(row.code);
      this.copiedId = row.id;
      setTimeout(() => {
        this.copiedId = null;
      }, 2000);
    },

    /* ============ Resend ============ */
    async resend(row) {
      this.resendingId = row.user_id;
      try {
        await otpService.resendOtp(row.user_id, {
          channel: row.channel ?? null,
        });
        await this.fetchOtps();
      } catch (e) {
        console.error("Resend error", e);
      } finally {
        this.resendingId = null;
      }
    },

    /* ============ Delete ============ */
    async deleteOtp(row) {
      const result = await alertConfirm(this.$t("delete_confirm"));
      if (!result.isConfirmed) return;

      this.deletingId = row.user_id;
      try {
        await otpService.deleteOtps(row.user_id);
        this.otps = this.otps.filter((o) => o.user_id !== row.user_id);
        this.meta.total = (this.meta.total ?? 1) - 1;
        alertSuccess(this.$t("deleted_successfully"));
      } catch (e) {
        alertError(this.$t("delete_failed"));
      } finally {
        this.deletingId = null;
      }
    },

    /* ============ Helpers ============ */
    statusLabel(status) {
      return (
        { active: "نشط", expired: "منتهي", used: "مستخدم" }[status] ?? status
      );
    },

    formatTime(iso) {
      if (!iso) return "—";
      return new Date(iso).toLocaleString("ar-EG", {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    expiresLabel(row) {
      if (row.status === "used") return "—";
      if (row.status === "expired") return "منتهي";
      const mins = Math.round((new Date(row.expires_at) - Date.now()) / 60000);
      return `ينتهي بعد ${mins} د`;
    },

    isExpiringSoon(row) {
      if (row.status !== "active") return false;
      return (new Date(row.expires_at) - Date.now()) / 60000 <= 3;
    },
  },

  async mounted() {
    await this.fetchOtps(1);
  },
};
</script>
